import {useMemo} from 'react'
import {useQueries} from '@tanstack/react-query'
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'
import {getProductsByIds, getCustomProductPrice} from '@/helpers/sfccHelpers'
import {isProduction} from '@/utils/common'
import {TEN_MINUTES_IN_MS} from '@/utils/constants'
import {useAppOrigin} from '~/app/hooks/use-app-origin'

const chunkArray = (array, size) => {
    return array?.reduce((acc, _, i) => (i % size ? acc : [...acc, array.slice(i, i + size)]), [])
}
export const useFetchBatchedSfccProducts = (productIds = []) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const productIdChunks = useMemo(() => chunkArray(productIds, 24), [productIds])
    const queries = useQueries({
        queries: productIdChunks.map((ids, index) => ({
            queryKey: ['sfcc-products', 'batched', index, ids],
            queryFn: () => getProductsByIds(ids, api, getTokenWhenReady),
            enabled: ids.length > 0,
            staleTime: isProduction ? TEN_MINUTES_IN_MS : 0
        }))
    })
    const isLoading =
        queries.length === 0 || queries.some((query) => query.isFetching || query.isLoading)
    const sfccProducts = queries.flatMap((query) => query.data?.data || [])
    return {sfccProducts, isSfccProductsLoading: isLoading}
}
export const useFetchBatchedProductPrices = (productIds = []) => {
    const productIdChunks = useMemo(() => chunkArray(productIds, 24), [productIds])
    const appOrigin = useAppOrigin()
    const queries = useQueries({
        queries: productIdChunks.map((ids, index) => ({
            queryKey: ['sfcc-product-prices', 'batched', index, ids],
            queryFn: () => getCustomProductPrice(ids, appOrigin),
            enabled: ids.length > 0,
            staleTime: isProduction ? TEN_MINUTES_IN_MS : 0
        }))
    })
    const isLoading =
        queries.length === 0 || queries.some((query) => query.isFetching || query.isLoading)
    const customPrices = queries
        .flatMap((query) => query.data?.data || [])
        .flatMap((data) => data?.products)
    return {customPrices, isProductPricesLoading: isLoading}
}
